// YearlyTable.js — Year-by-year projection detail table
import React, { useState } from 'react';

/**
 * Format number as compact currency.
 */
function fmtVal(v, ccy) {
  if (v === null || v === undefined) return '--';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const sym = ccy === 'GBP' ? '\u00a3' : ccy === 'EUR' ? '\u20ac' : '$';
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/**
 * Year-by-year detail table with scenario + hedged/unhedged selectors.
 * @param {{ result: Object, baseCurrency: string }} props
 */
const YearlyTable = ({ result, baseCurrency = 'USD' }) => {
  const [selectedScenario, setSelectedScenario] = useState('base');
  const [showHedged, setShowHedged] = useState(true);

  if (!result || !result.scenarios) return null;

  const availableScenarios = Object.keys(result.scenarios);
  const scenario = result.scenarios[selectedScenario] || result.scenarios[availableScenarios[0]];
  if (!scenario) return null;

  const valueKey = showHedged ? 'valueHedged' : 'valueUnhedged';
  const { benchmark } = result;

  const columns = [
    { key: 'year', label: 'Year', align: 'left' },
    { key: 'value', label: 'Value', align: 'right' },
    { key: 'income', label: 'Income', align: 'right' },
    { key: 'withdrawal', label: 'Withdrawals', align: 'right' },
    { key: 'fees', label: 'Fees', align: 'right' },
    { key: 'taxes', label: 'Taxes', align: 'right' },
    { key: 'fxImpact', label: 'Foreign Exchange Impact', align: 'right' },
    { key: 'forwardCost', label: 'Fwd Cost', align: 'right' },
    { key: 'benchmark', label: 'Benchmark', align: 'right' },
    { key: 'alpha', label: 'Alpha', align: 'right' },
  ];

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
          Year-by-Year Detail
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scenario selector */}
          <select
            value={selectedScenario}
            onChange={e => setSelectedScenario(e.target.value)}
            className="font-mono text-[10px] px-2 py-1 border border-zinc-200 rounded-none bg-white text-zinc-700"
            aria-label="Select scenario"
          >
            {availableScenarios.map(s => (
              <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
            ))}
          </select>
          {/* Hedged/Unhedged toggle */}
          <button
            onClick={() => setShowHedged(true)}
            className={`font-mono text-[10px] px-2 py-1 border rounded-none ${
              showHedged ? 'bg-zinc-600 text-white border-zinc-600' : 'text-zinc-500 border-zinc-300 hover:bg-zinc-50'
            }`}
          >
            Hedged
          </button>
          <button
            onClick={() => setShowHedged(false)}
            className={`font-mono text-[10px] px-2 py-1 border rounded-none ${
              !showHedged ? 'bg-zinc-600 text-white border-zinc-600' : 'text-zinc-500 border-zinc-300 hover:bg-zinc-50'
            }`}
          >
            Unhedged
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left" aria-label="Yearly projection detail">
          <thead>
            <tr className="border-b border-zinc-200">
              {columns.map(col => (
                <th
                  key={col.key}
                  className={`font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 py-2 px-2 font-medium ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scenario.years.map((y, idx) => {
              const bmVal = benchmark && benchmark.years[idx] ? benchmark.years[idx].value : 0;
              const val = y[valueKey] || 0;
              const alpha = val - bmVal;

              return (
                <tr key={y.year} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 tabular-nums">{y.year}</td>
                  <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 text-right tabular-nums">{fmtVal(val, baseCurrency)}</td>
                  <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 text-right tabular-nums">{fmtVal(y.income, baseCurrency)}</td>
                  <td className="font-mono text-[11px] text-zinc-700 py-2 px-2 text-right tabular-nums">{fmtVal(y.withdrawal, baseCurrency)}</td>
                  <td className="font-mono text-[11px] text-zinc-500 py-2 px-2 text-right tabular-nums">{fmtVal(y.fees, baseCurrency)}</td>
                  <td className="font-mono text-[11px] text-zinc-500 py-2 px-2 text-right tabular-nums">{fmtVal(y.taxes, baseCurrency)}</td>
                  <td className={`font-mono text-[11px] py-2 px-2 text-right tabular-nums ${
                    y.fxImpact >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {fmtVal(y.fxImpact, baseCurrency)}
                  </td>
                  <td className="font-mono text-[11px] text-zinc-500 py-2 px-2 text-right tabular-nums">{fmtVal(y.forwardCost, baseCurrency)}</td>
                  <td className="font-mono text-[11px] text-zinc-400 py-2 px-2 text-right tabular-nums">{fmtVal(bmVal, baseCurrency)}</td>
                  <td className={`font-mono text-[11px] py-2 px-2 text-right tabular-nums font-medium ${
                    alpha >= 0 ? 'text-emerald-600' : 'text-red-600'
                  }`}>
                    {fmtVal(alpha, baseCurrency)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default YearlyTable;
